import React from 'react'
import {DealDetailsButton} from 'components/Accommodation/DealDetails'
import {useTrackPreferredRateOfferServed} from 'hooks/useTrackPreferredRateOfferServed'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Card} from '@vio/atlas/src/components/designSystem/Card'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {
  getCancellationDeadline,
  getRefundType
} from '@vio/core/src/offer/business/cancellationPenalties'
import {getOfferPaymentMode} from '@vio/core/src/offer/business/offerPaymentMode'
import {hasOfferTag} from '@vio/core/src/offer/business/offers'
import {OfferTagLabel, RateChecker} from '@vio/core/src/offer/types/offer'
import {OfferPerks} from '@vio/shared/src/accommodation/OfferPerks/OfferPerks'
import {Cashback} from '@vio/shared/src/cashback/components/Cashback'

import {RoomDetailsMountComponent} from '../RoomCard/constants'
import {RoomOfferLayout} from '../RoomOffer/RoomOfferLayout'
import {RoomOfferSelectedLabel} from '../RoomOffer/RoomOfferSelectedLabel'
import {RoomOfferCTA} from '../RoomOfferCTA'
import {RoomsOfferPrice} from './RoomsOfferPrice'

import type {CancellationPenalties} from '@findhotel/sapi'
import type {SplitBooking} from '@vio/core/src/room/types/room'

interface Props {
  hotelId: string
  hotelTimeZone: string
  cancellationPenalties: CancellationPenalties[]
  splitBooking: SplitBooking
  isOfferSoldOut?: boolean
}

const FooterWrapper = ({children}: {children: React.ReactNode}) => {
  const {isMobile} = useDeviceLayout()
  return (
    <Card
      borderColorPath="border.neutral.secondary"
      borderRadius="lg"
      isOverflowHidden={false}
    >
      <Box padding={isMobile ? 's500' : 's600'} data-id="room-offer-container">
        {children}
      </Box>
    </Card>
  )
}

export const SplitBookingCardFooter = ({
  hotelId,
  cancellationPenalties,
  hotelTimeZone,
  splitBooking,
  isOfferSoldOut = false
}: Props) => {
  const {brandName, brandSoldOutLabelMessage} = useBrand()
  const {
    monetisation: {cashback}
  } = useBrandCapabilities()
  const {isMobile} = useDeviceLayout()

  const isYourChoice = hasOfferTag(splitBooking, OfferTagLabel.IsYourChoice)
  useTrackPreferredRateOfferServed(isYourChoice)
  const isSelected = splitBooking.isClicked || isYourChoice
  const showYourSelectionLabel = isSelected && !isOfferSoldOut

  const cancellationDeadline = getCancellationDeadline(cancellationPenalties)
  const refundType = getRefundType(cancellationPenalties)
  const paymentType = getOfferPaymentMode({
    offer: splitBooking.offers[0].offer,
    isSplitBooking: true
  })

  return (
    <Box
      paddingLeft={isMobile ? 's500' : 's600'}
      paddingRight={isMobile ? 's500' : 's600'}
      paddingTop="s000"
      paddingBottom={isMobile ? 's500' : 's600'}
      data-id={isYourChoice && RateChecker.GHAYourChoiceId}
    >
      <FooterWrapper>
        <RoomOfferLayout
          isVerticalLayout={isMobile}
          detailsSlot={
            <Box
              display="flex"
              alignItems="flex-start"
              flexDirection="column"
              justifyContent="center"
              gap="s200"
            >
              {isOfferSoldOut && (
                <Box
                  display="flex"
                  justifyContent="flex-start"
                  alignItems="center"
                  gap="s250"
                  paddingBottom="s250"
                >
                  <Icon
                    name="Forbid"
                    size="sm"
                    colorPath="content.danger.secondary"
                  />

                  <Text variant="labelM" colorPath="content.danger.secondary">
                    <FormattedMessageWrapper
                      {...brandSoldOutLabelMessage}
                      values={{brandName}}
                    />
                  </Text>
                </Box>
              )}

              {showYourSelectionLabel && (
                <Box paddingBottom="s200">
                  <RoomOfferSelectedLabel />
                </Box>
              )}
              <OfferPerks
                paymentType={paymentType}
                refundType={refundType}
                cancellationDeadline={cancellationDeadline}
                timezone={hotelTimeZone}
                isOfferSoldOut={isOfferSoldOut}
              />
              <DealDetailsButton bundleId={splitBooking.bundleId} />
            </Box>
          }
          priceSlot={
            <Box
              display="flex"
              gap={cssTheme.layout.spacing.s300}
              flexDirection="column"
            >
              <RoomsOfferPrice
                hotelId={hotelId}
                prices={splitBooking?.prices || []}
                isOfferSoldOut={isOfferSoldOut}
                isRightAligned={isMobile}
              />
              {cashback.enabled && splitBooking?.cashback && (
                <Box marginTop="s100" display="flex" justifyContent="flex-end">
                  <Cashback
                    value={splitBooking?.cashback?.value}
                    type={splitBooking?.cashback?.type}
                  />
                </Box>
              )}
              <RoomOfferCTA
                hotelId={hotelId}
                component={RoomDetailsMountComponent.HotelViewRoomsSection}
                shouldShowDealFreezeButton={false}
                splitBooking={splitBooking}
              />
            </Box>
          }
        />
      </FooterWrapper>
    </Box>
  )
}
